import React, {useEffect, useState, useMemo, useRef } from 'react'
import Sidebar from '../components/Sidebar'
import Header from '../components/Header'

import ReactQuill from 'react-quill-new';
import Swal from 'sweetalert2';

import { usePagination } from "../hooks/usePagination";
import UniversalPagination from "../common/UniversalPagination";

import { API_BASE } from "../config/api";

const initialAvisoForm = {
    aviso_texto: "",
    aviso_boton_texto: "",
    aviso_boton_url: "",
    aviso_fecha_inicio: "",
    aviso_fecha_fin: "",
    aviso_activo: 1,
};

const initialEditAvisoForm = {
    aviso_id: "",
    aviso_texto: "",
    aviso_boton_texto: "",
    aviso_boton_url: "",
    aviso_fecha_inicio: "",
    aviso_fecha_fin: "",
    aviso_activo: 1,
};

const monthOptions = [
    { value: "05", label: "Mayo" },
    { value: "06", label: "Junio" },
    { value: "07", label: "Julio" },
    { value: "08", label: "Agosto" },
    { value: "09", label: "Septiembre" },
    { value: "10", label: "Octubre" },
    { value: "11", label: "Noviembre" },
    { value: "12", label: "Diciembre" },
];

function CintilloList() {

    const [avisosList, setAvisosList] = useState([]);
    const [loadingAvisos, setLoadingAvisos] = useState(false);
    const [avisosError, setAvisosError] = useState("");

    const [avisoForm, setAvisoForm] = useState(initialAvisoForm);
    const [addingAviso, setAddingAviso] = useState(false);

    const [avisoSuccessMessage, setAvisoSuccessMessage] = useState("");
    const [avisoInfoMessage, setAvisoInfoMessage] = useState("");
    const [avisoErrorMessage, setAvisoErrorMessage] = useState("");

    const [viewAvisoData, setViewAvisoData] = useState(null);
    const [loadingViewAviso, setLoadingViewAviso] = useState(false);
    const [viewAvisoError, setViewAvisoError] = useState("");

    const [editAvisoForm, setEditAvisoForm] = useState(initialEditAvisoForm);
    const [loadingEditAviso, setLoadingEditAviso] = useState(false);
    const [savingEditAviso, setSavingEditAviso] = useState(false);

    const [editAvisoSuccessMessage, setEditAvisoSuccessMessage] = useState("");
    const [editAvisoInfoMessage, setEditAvisoInfoMessage] = useState("");
    const [editAvisoErrorMessage, setEditAvisoErrorMessage] = useState("");

    const [avisoSearchTerm, setAvisoSearchTerm] = useState("");
    const [avisoDateFilterMode, setAvisoDateFilterMode] = useState("basic");
    const [avisoMonthFilter, setAvisoMonthFilter] = useState("");
    const [avisoStartDateFilter, setAvisoStartDateFilter] = useState("");
    const [avisoEndDateFilter, setAvisoEndDateFilter] = useState("");

    const loadAvisos = async () => {
        try {
            setLoadingAvisos(true);
            setAvisosError("");

            const response = await fetch(`${API_BASE}/api/admin/widgets/avisos`, {
                method: "GET",
                credentials: "include",
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data?.message || "No fue posible cargar los avisos");
            }

            setAvisosList(Array.isArray(data?.data) ? data.data : []);
        } catch (error) {
            console.error("Error al cargar avisos:", error);
            setAvisosList([]);
            setAvisosError(error.message || "Ocurrió un error al cargar los avisos");
        } finally {
            setLoadingAvisos(false);
        }
    };

    useEffect(() => {
        loadAvisos();
    }, []);

    // Helpers
    const resetAvisoMessages = () => {
        setAvisoSuccessMessage("");
        setAvisoInfoMessage("");
        setAvisoErrorMessage("");
    };

    const resetAvisoForm = () => {
        setAvisoForm(initialAvisoForm);
    };

    const resetAddAvisoModal = () => {
        resetAvisoMessages();
        resetAvisoForm();
        setAddingAviso(false);
    };

    const resetViewAvisoModal = () => {
        setViewAvisoData(null);
        setLoadingViewAviso(false);
        setViewAvisoError("");
    };

    const formatDateTimeLocal = (value) => {
    if (!value) return "";

    const cleanValue = String(value)
        .replace("T", " ")
        .replace("Z", "")
        .replace(/\.\d+$/, "")
        .trim();

    const [datePart, timePart = "00:00"] = cleanValue.split(" ");
    const [hours = "00", minutes = "00"] = timePart.split(":");

    return `${datePart}T${hours}:${minutes}`;
};

    const formatAvisoDateTime = (value) => {
        if (!value) return "-";

        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return "-";

        return date.toLocaleString("es-MX", {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
        });
    };

    const formatAvisoDate = (value) => {
        if (!value) return "-";

        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return "-";

        return date.toLocaleString("es-MX", {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
        });
    };

    const getAvisoStatus = (aviso) => {
        const isActive = Number(aviso.Aviso_Activo) === 1;

        if (!isActive) return "inactive";

        const now = new Date();

        const startDate = aviso.Aviso_Fecha_Inicio
            ? new Date(aviso.Aviso_Fecha_Inicio)
            : null;

        const endDate = aviso.Aviso_Fecha_Fin
            ? new Date(aviso.Aviso_Fecha_Fin)
            : null;

        if (startDate && !Number.isNaN(startDate.getTime()) && now < startDate) {
            return "scheduled";
        }

        if (endDate && !Number.isNaN(endDate.getTime()) && now > endDate) {
            return "finished";
        }

        return "active";
    };

    const renderAvisoStatusBadge = (aviso) => {
        const status = getAvisoStatus(aviso);

        if (status === "scheduled") {
            return (
                <span className="badge bg-warning-subtle text-warning p-2">
                    Programado
                </span>
            );
        }

        if (status === "active") {
            return (
                <span className="badge bg-success-subtle text-success p-2">
                    Activo
                </span>
            );
        }

        if (status === "finished") {
            return (
                <span className="badge bg-secondary-subtle text-secondary p-2">
                    Finalizado
                </span>
            );
        }

        return (
            <span className="badge bg-danger-subtle text-danger p-2">
                Inactivo
            </span>
        );
    };

    const resetEditAvisoMessages = () => {
        setEditAvisoSuccessMessage("");
        setEditAvisoInfoMessage("");
        setEditAvisoErrorMessage("");
    };

    const resetEditAvisoForm = () => {
        setEditAvisoForm(initialEditAvisoForm);
    };

    const resetEditAvisoModal = () => {
        resetEditAvisoMessages();
        resetEditAvisoForm();
        setLoadingEditAviso(false);
        setSavingEditAviso(false);
    };

    const handleEditAvisoInputChange = (event) => {
        const { name, value } = event.target;

        setEditAvisoForm((prev) => ({
            ...prev,
            [name]: name === "aviso_activo" ? Number(value) : value,
        }));
    };

    // Funcion para ver detalle de Tira de avisos
    const handleViewAviso = async (avisoId) => {
        try {
            setLoadingViewAviso(true);
            setViewAvisoError("");
            setViewAvisoData(null);

            const response = await fetch(`${API_BASE}/api/admin/widgets/avisos/${avisoId}`, {
                method: "GET",
                credentials: "include",
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data?.message || "No fue posible obtener el detalle del aviso");
            }

            setViewAvisoData(data?.data || null);
        } catch (error) {
            console.error("Error al obtener detalle del aviso:", error);
            setViewAvisoError(error.message || "Ocurrió un error al obtener el detalle del aviso");
        } finally {
            setLoadingViewAviso(false);
        }
    };

    // Funcion para crear Tira de avisos
    const handleAvisoInputChange = (event) => {
        const { name, value } = event.target;

        setAvisoForm((prev) => ({
            ...prev,
            [name]: name === "aviso_activo" ? Number(value) : value,
        }));
    };

    const validateAvisoForm = () => {
        if (!avisoForm.aviso_texto.trim()) {
            return "El texto del aviso es requerido";
        }

        if (avisoForm.aviso_texto.trim().length > 255) {
            return "El texto del aviso no puede superar 255 caracteres";
        }

        if (!avisoForm.aviso_fecha_inicio) {
            return "La fecha de inicio es requerida";
        }

        if (!avisoForm.aviso_fecha_fin) {
            return "La fecha de fin es requerida";
        }

        const inicio = new Date(avisoForm.aviso_fecha_inicio);
        const fin = new Date(avisoForm.aviso_fecha_fin);

        if (Number.isNaN(inicio.getTime())) {
            return "La fecha de inicio no es válida";
        }

        if (Number.isNaN(fin.getTime())) {
            return "La fecha de fin no es válida";
        }

        if (fin.getTime() < inicio.getTime()) {
            return "La fecha de fin no puede ser menor que la fecha de inicio";
        }

        return null;
    };

    const handleAddAviso = async (event) => {
        event.preventDefault();
        resetAvisoMessages();

        const validationError = validateAvisoForm();

        if (validationError) {
            setAvisoInfoMessage(validationError);
            return;
        }

        try {
            setAddingAviso(true);

            const payload = {
                aviso_texto: avisoForm.aviso_texto.trim(),
                aviso_boton_texto: avisoForm.aviso_boton_texto.trim() || null,
                aviso_boton_url: avisoForm.aviso_boton_url.trim() || null,
                aviso_fecha_inicio: String(avisoForm.aviso_fecha_inicio || "").replace("T", " "),
                aviso_fecha_fin: String(avisoForm.aviso_fecha_fin || "").replace("T", " "),
                aviso_activo: Number(avisoForm.aviso_activo ?? 1),
            };

            const response = await fetch(`${API_BASE}/api/admin/widgets/avisos`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                credentials: "include",
                body: JSON.stringify(payload),
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data?.message || "No fue posible agregar el aviso");
            }

            setAvisoSuccessMessage(data?.message || "Aviso agregado correctamente");

            await loadAvisos();

            setTimeout(() => {
                const modalElement = document.getElementById("addTiraModal");

                if (modalElement && window.bootstrap?.Modal) {
                    const modalInstance =
                        window.bootstrap.Modal.getInstance(modalElement) ||
                        new window.bootstrap.Modal(modalElement);

                    modalInstance.hide();
                }

                resetAddAvisoModal();
            }, 1200);
        } catch (error) {
            console.error("Error al agregar aviso:", error);
            setAvisoErrorMessage(error.message || "Ocurrió un error al agregar el aviso");
        } finally {
            setAddingAviso(false);
        }
    };

    // Funcion para editar Tira de avisos
    const handleLoadEditAviso = async (avisoId) => {
        try {
            setLoadingEditAviso(true);
            resetEditAvisoMessages();
            resetEditAvisoForm();

            const response = await fetch(`${API_BASE}/api/admin/widgets/avisos/${avisoId}`, {
                method: "GET",
                credentials: "include",
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data?.message || "No fue posible obtener el detalle del aviso");
            }

            const aviso = data?.data;

            setEditAvisoForm({
                aviso_id: aviso?.Aviso_ID || "",
                aviso_texto: aviso?.Aviso_Texto || "",
                aviso_boton_texto: aviso?.Aviso_Boton_Texto || "",
                aviso_boton_url: aviso?.Aviso_Boton_URL || "",
                aviso_fecha_inicio: formatDateTimeLocal(aviso?.Aviso_Fecha_Inicio),
                aviso_fecha_fin: formatDateTimeLocal(aviso?.Aviso_Fecha_Fin),
                aviso_activo: Number(aviso?.Aviso_Activo ?? 1),
            });
        } catch (error) {
            console.error("Error al cargar aviso para editar:", error);
            setEditAvisoErrorMessage(
                error.message || "Ocurrió un error al cargar el aviso"
            );
        } finally {
            setLoadingEditAviso(false);
        }
    };

    const validateEditAvisoForm = () => {
        if (!editAvisoForm.aviso_texto.trim()) {
            return "El texto del aviso es requerido";
        }

        if (editAvisoForm.aviso_texto.trim().length > 255) {
            return "El texto del aviso no puede superar 255 caracteres";
        }

        if (!editAvisoForm.aviso_fecha_inicio) {
            return "La fecha de inicio es requerida";
        }

        if (!editAvisoForm.aviso_fecha_fin) {
            return "La fecha de fin es requerida";
        }

        const inicio = new Date(editAvisoForm.aviso_fecha_inicio);
        const fin = new Date(editAvisoForm.aviso_fecha_fin);

        if (Number.isNaN(inicio.getTime())) {
            return "La fecha de inicio no es válida";
        }

        if (Number.isNaN(fin.getTime())) {
            return "La fecha de fin no es válida";
        }

        if (fin.getTime() < inicio.getTime()) {
            return "La fecha de fin no puede ser menor que la fecha de inicio";
        }

        return null;
    };

    const handleUpdateAviso = async (event) => {
        event.preventDefault();
        resetEditAvisoMessages();

        const validationError = validateEditAvisoForm();

        if (validationError) {
            setEditAvisoInfoMessage(validationError);
            return;
        }

        try {
            setSavingEditAviso(true);

            const payload = {
                aviso_texto: editAvisoForm.aviso_texto.trim(),
                aviso_boton_texto: editAvisoForm.aviso_boton_texto.trim() || null,
                aviso_boton_url: editAvisoForm.aviso_boton_url.trim() || null,
                aviso_fecha_inicio: String(editAvisoForm.aviso_fecha_inicio || "").replace("T", " "),
                aviso_fecha_fin: String(editAvisoForm.aviso_fecha_fin || "").replace("T", " "),
                aviso_activo: Number(editAvisoForm.aviso_activo ?? 1),
            };

            const response = await fetch(
                `${API_BASE}/api/admin/widgets/avisos/${editAvisoForm.aviso_id}`,
                {
                    method: "PUT",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    credentials: "include",
                    body: JSON.stringify(payload),
                }
            );

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data?.message || "No fue posible actualizar el aviso");
            }

            setEditAvisoSuccessMessage(data?.message || "Aviso actualizado correctamente");

            await loadAvisos();

            setTimeout(() => {
                const modalElement = document.getElementById("editAvisoModal");

                if (modalElement && window.bootstrap?.Modal) {
                    const modalInstance =
                        window.bootstrap.Modal.getInstance(modalElement) ||
                        new window.bootstrap.Modal(modalElement);

                    modalInstance.hide();
                }

                resetEditAvisoModal();
            }, 1200);
        } catch (error) {
            console.error("Error al actualizar aviso:", error);
            setEditAvisoErrorMessage(
                error.message || "Ocurrió un error al actualizar el aviso"
            );
        } finally {
            setSavingEditAviso(false);
        }
    };

    // Funcion para eliminar Tira de avisos
    const handleDeleteAviso = async (avisoId, avisoTexto) => {
        try {
            const result = await Swal.fire({
                title: "¿Seguro que quieres eliminar este elemento?",
                text: `Se desactivará "${avisoTexto}". Esta función es irreversible.`,
                icon: "warning",
                showCancelButton: true,
                confirmButtonText: "Confirmar",
                cancelButtonText: "Cancelar",
                reverseButtons: true,
                confirmButtonColor: "#3085d6",
                cancelButtonColor: "#d33",
            });

            if (!result.isConfirmed) return;

            const response = await fetch(
                `${API_BASE}/api/admin/widgets/avisos/${avisoId}/status`,
                {
                    method: "PATCH",
                    credentials: "include",
                }
            );

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data?.message || "No fue posible desactivar el aviso");
            }

            await Swal.fire({
                title: "Aviso eliminado",
                text: data?.message || "Aviso desactivado correctamente.",
                icon: "success",
                confirmButtonText: "Aceptar",
                confirmButtonColor: "#3085d6",
            });

            await loadAvisos();
        } catch (error) {
            console.error("Error al desactivar aviso:", error);

            await Swal.fire({
                title: "Error",
                text: error.message || "Ocurrió un error al desactivar el aviso",
                icon: "error",
                confirmButtonText: "Aceptar",
                confirmButtonColor: "#d33",
            });
        }
    };

    useEffect(() => {
        const modalElement = document.getElementById("addTiraModal");

        if (!modalElement) return;

        const handleHiddenModal = () => {
            resetAddAvisoModal();
        };

        modalElement.addEventListener("hidden.bs.modal", handleHiddenModal);

        return () => {
            modalElement.removeEventListener("hidden.bs.modal", handleHiddenModal);
        };
    }, []);

    useEffect(() => {
        const modalElement = document.getElementById("viewAvisoModal");

        if (!modalElement) return;

        const handleHiddenModal = () => {
            resetViewAvisoModal();
        };

        modalElement.addEventListener("hidden.bs.modal", handleHiddenModal);

        return () => {
            modalElement.removeEventListener("hidden.bs.modal", handleHiddenModal);
        };
    }, []);

    useEffect(() => {
        const modalElement = document.getElementById("editAvisoModal");

        if (!modalElement) return;

        const handleHiddenModal = () => {
            resetEditAvisoModal();
        };

        modalElement.addEventListener("hidden.bs.modal", handleHiddenModal);

        return () => {
            modalElement.removeEventListener("hidden.bs.modal", handleHiddenModal);
        };
    }, []);

    // Filtros
    const filteredAvisosList = useMemo(() => {
        return avisosList.filter((aviso) => {
            const search = avisoSearchTerm.trim().toLowerCase();

            const texto = String(aviso.Aviso_Texto || "").toLowerCase();
            const botonTexto = String(aviso.Aviso_Boton_Texto || "").toLowerCase();
            const botonUrl = String(aviso.Aviso_Boton_URL || "").toLowerCase();

            const matchesSearch =
                !search ||
                texto.includes(search) ||
                botonTexto.includes(search) ||
                botonUrl.includes(search);

            let matchesDate = true;

            const avisoStartDate = aviso.Aviso_Fecha_Inicio
                ? new Date(aviso.Aviso_Fecha_Inicio)
                : null;

            if (avisoDateFilterMode === "basic" && avisoMonthFilter) {
                if (!avisoStartDate || Number.isNaN(avisoStartDate.getTime())) {
                    matchesDate = false;
                } else {
                    const avisoMonth = String(avisoStartDate.getMonth() + 1).padStart(2, "0");
                    matchesDate = avisoMonth === avisoMonthFilter;
                }
            }

            if (avisoDateFilterMode === "advanced") {
                if (avisoStartDateFilter) {
                    const start = new Date(`${avisoStartDateFilter}T00:00:00`);

                    if (!avisoStartDate || avisoStartDate < start) {
                        matchesDate = false;
                    }
                }

                if (avisoEndDateFilter) {
                    const end = new Date(`${avisoEndDateFilter}T23:59:59`);

                    if (!avisoStartDate || avisoStartDate > end) {
                        matchesDate = false;
                    }
                }
            }

            return matchesSearch && matchesDate;
        });
    }, [
        avisosList,
        avisoSearchTerm,
        avisoDateFilterMode,
        avisoMonthFilter,
        avisoStartDateFilter,
        avisoEndDateFilter,
    ]);

    // Paginador
    const {
        currentPage,
        totalPages,
        totalItems,
        paginatedItems,
        visiblePages,
        startItem,
        endItem,
        goToPage,
        goToPreviousPage,
        goToNextPage,
        resetPagination,
        hasPreviousPage,
        hasNextPage,
    } = usePagination(filteredAvisosList, 8, 5);

    useEffect(() => {
        resetPagination();
    }, [
        avisoSearchTerm,
        avisoDateFilterMode,
        avisoMonthFilter,
        avisoStartDateFilter,
        avisoEndDateFilter,
    ]);

    const handleClearAvisoFilters = () => {
        setAvisoSearchTerm("");
        setAvisoDateFilterMode("basic");
        setAvisoMonthFilter("");
        setAvisoStartDateFilter("");
        setAvisoEndDateFilter("");
    };

    return (
        <>
        <Sidebar/>
        <main class="main-content">
            <Header/>

            <div class="content-inner container-fluid pb-0" id="page_layout">
                <div class="d-flex justify-content-between align-items-center flex-wrap mb-4 gap-3">
                    <div class="d-flex flex-column">
                        <h3>Tira de avisos  </h3>
                        <p class="text-primary mb-0">Panduit Core</p>
                    </div>
                </div>
            

                <div class="row">
                    <div class="col-sm-12">
                        <div class="card">
                            <div className="card-header d-flex justify-content-end">
                                <div className="row g-2 align-items-end w-100">
                                    <div className="col-md-4">
                                        <label className="form-label mb-1">Buscar</label>
                                        <input
                                            type="text"
                                            className="form-control form-control-sm"
                                            placeholder="Título o descripción..."
                                        />
                                    </div>

                                    <div className="col-md-2">
                                        <label className="form-label mb-1 d-block">Tipo fecha</label>

                                        <div className="form-check form-switch mt-2">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                id="avisoDateFilterSwitch"
                                                checked={avisoDateFilterMode === "advanced"}
                                                onChange={(e) => {
                                                    const isAdvanced = e.target.checked;

                                                    setAvisoDateFilterMode(isAdvanced ? "advanced" : "basic");
                                                    setAvisoMonthFilter("");
                                                    setAvisoStartDateFilter("");
                                                    setAvisoEndDateFilter("");
                                                }}
                                            />

                                            <label
                                                className="form-check-label small"
                                                htmlFor="avisoDateFilterSwitch"
                                            >
                                                {avisoDateFilterMode === "advanced" ? "Avanzado" : "Básico"}
                                            </label>
                                        </div>
                                    </div>

                                    {avisoDateFilterMode === "basic" ? (
                                        <div className="col-md-3">
                                            <label className="form-label mb-1">Mes</label>
                                            <select
                                                className="form-select form-select-sm"
                                                value={avisoMonthFilter}
                                                onChange={(e) => setAvisoMonthFilter(e.target.value)}
                                            >
                                                <option value="">Todos</option>
                                                {monthOptions.map((month) => (
                                                    <option key={month.value} value={month.value}>
                                                        {month.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    ) : (
                                        <>
                                            <div className="col-md-3">
                                                <label className="form-label mb-1">Desde</label>
                                                <input
                                                    type="date"
                                                    className="form-control form-control-sm"
                                                    value={avisoStartDateFilter}
                                                    onChange={(e) => setAvisoStartDateFilter(e.target.value)}
                                                />
                                            </div>

                                            <div className="col-md-3">
                                                <label className="form-label mb-1">Hasta</label>
                                                <input
                                                    type="date"
                                                    className="form-control form-control-sm"
                                                    value={avisoEndDateFilter}
                                                    onChange={(e) => setAvisoEndDateFilter(e.target.value)}
                                                />
                                            </div>
                                        </>
                                    )}

                                    <div className="col-md-3 d-flex justify-content-end gap-2 ms-auto">
                                        <button
                                            type="button"
                                            className="btn btn-secondary-subtle btn-sm"
                                            title="Limpiar filtros"
                                            onClick={handleClearAvisoFilters}
                                        >
                                        <svg width="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <g opacity="0.4">
                                            <path
                                                d="M4.88076 14.6713C4.74978 14.2784 4.32504 14.066 3.93208 14.197C3.53912 14.328 3.32675 14.7527 3.45774 15.1457L4.88076 14.6713ZM20.8808 15.1457C21.0117 14.7527 20.7994 14.328 20.4064 14.197C20.0135 14.066 19.5887 14.2784 19.4577 14.6713L20.8808 15.1457ZM4.16925 14.9085C3.45774 15.1457 3.45785 15.146 3.45797 15.1464C3.45802 15.1465 3.45815 15.1469 3.45825 15.1472C3.45845 15.1478 3.45868 15.1485 3.45895 15.1493C3.45948 15.1509 3.46013 15.1528 3.46092 15.1551C3.46249 15.1597 3.46456 15.1657 3.46716 15.1731C3.47235 15.188 3.47961 15.2084 3.48902 15.2341C3.50782 15.2854 3.53521 15.3576 3.5717 15.4477C3.64461 15.6279 3.7542 15.8805 3.90468 16.1814C4.2048 16.7817 4.67223 17.5836 5.34308 18.3886C6.68942 20.0043 8.88343 21.6585 12.1693 21.6585V20.1585C9.45507 20.1585 7.64908 18.8128 6.49542 17.4284C5.91627 16.7334 5.5087 16.0354 5.24632 15.5106C5.11555 15.2491 5.02201 15.0329 4.96212 14.8849C4.9322 14.811 4.91076 14.7543 4.89733 14.7177C4.89062 14.6994 4.88593 14.6861 4.88318 14.6783C4.88181 14.6744 4.88093 14.6718 4.88053 14.6706C4.88033 14.67 4.88025 14.6698 4.88029 14.6699C4.88031 14.67 4.88036 14.6701 4.88044 14.6704C4.88047 14.6705 4.88056 14.6707 4.88058 14.6708C4.88067 14.671 4.88076 14.6713 4.16925 14.9085ZM12.1693 21.6585C15.4551 21.6585 17.6491 20.0043 18.9954 18.3886C19.6663 17.5836 20.1337 16.7817 20.4338 16.1814C20.5843 15.8805 20.6939 15.6279 20.7668 15.4477C20.8033 15.3576 20.8307 15.2854 20.8495 15.2341C20.8589 15.2084 20.8662 15.188 20.8713 15.1731C20.8739 15.1657 20.876 15.1597 20.8776 15.1551C20.8784 15.1528 20.879 15.1509 20.8796 15.1493C20.8798 15.1485 20.8801 15.1478 20.8803 15.1472C20.8804 15.1469 20.8805 15.1465 20.8805 15.1464C20.8807 15.146 20.8808 15.1457 20.1693 14.9085C19.4577 14.6713 19.4578 14.671 19.4579 14.6708C19.4579 14.6707 19.458 14.6705 19.4581 14.6704C19.4581 14.6701 19.4582 14.67 19.4582 14.6699C19.4583 14.6698 19.4582 14.67 19.458 14.6706C19.4576 14.6718 19.4567 14.6744 19.4553 14.6783C19.4526 14.6861 19.4479 14.6994 19.4412 14.7177C19.4277 14.7543 19.4063 14.811 19.3764 14.8849C19.3165 15.0329 19.223 15.2491 19.0922 15.5106C18.8298 16.0354 18.4222 16.7334 17.8431 17.4284C16.6894 18.8128 14.8834 20.1585 12.1693 20.1585V21.6585Z"
                                                fill="currentColor"></path>
                                            <path d="M21.5183 19.2271C21.4293 19.2234 21.3427 19.196 21.2671 19.1465L16.3546 15.8924C16.2197 15.8026 16.1413 15.6537 16.148 15.4969C16.1546 15.34 16.2452 15.1982 16.3873 15.1202L21.5571 12.2926C21.7075 12.2106 21.8932 12.213 22.0416 12.3003C22.1907 12.387 22.2783 12.5436 22.2712 12.7096L22.014 18.7913C22.007 18.9573 21.9065 19.1059 21.7506 19.1797C21.6772 19.215 21.597 19.2305 21.5183 19.2271" fill="currentColor"></path>
                                            </g>
                                            <path
                                            d="M20.0742 10.0265C20.1886 10.4246 20.6041 10.6546 21.0022 10.5401C21.4003 10.4257 21.6302 10.0102 21.5158 9.61214L20.0742 10.0265ZM4.10803 8.88317C3.96071 9.27031 4.15513 9.70356 4.54226 9.85087C4.92939 9.99818 5.36265 9.80377 5.50996 9.41664L4.10803 8.88317ZM20.795 9.81934C21.5158 9.61214 21.5157 9.6118 21.5156 9.61144C21.5155 9.61129 21.5154 9.6109 21.5153 9.61059C21.5152 9.60998 21.515 9.60928 21.5147 9.60848C21.5143 9.60689 21.5137 9.60493 21.513 9.6026C21.5116 9.59795 21.5098 9.59184 21.5075 9.58431C21.503 9.56925 21.4966 9.54853 21.4882 9.52251C21.4716 9.47048 21.4473 9.39719 21.4146 9.3056C21.3493 9.12256 21.2503 8.8656 21.1126 8.55861C20.8378 7.94634 20.4044 7.12552 19.7678 6.29313C18.4902 4.62261 16.3673 2.87801 13.0844 2.74053L13.0216 4.23922C15.7334 4.35278 17.4816 5.77291 18.5763 7.20436C19.1258 7.92295 19.5038 8.63743 19.744 9.17271C19.8638 9.43949 19.9482 9.65937 20.0018 9.80972C20.0286 9.88483 20.0477 9.94238 20.0596 9.97951C20.0655 9.99808 20.0696 10.0115 20.072 10.0195C20.0732 10.0235 20.074 10.0261 20.0744 10.0273C20.0746 10.0278 20.0746 10.0281 20.0746 10.028C20.0746 10.0279 20.0745 10.0278 20.0745 10.0275C20.0744 10.0274 20.0744 10.0272 20.0743 10.0271C20.0743 10.0268 20.0742 10.0265 20.795 9.81934ZM13.0844 2.74053C9.80146 2.60306 7.54016 4.16407 6.12741 5.72193C5.42345 6.49818 4.92288 7.27989 4.59791 7.86704C4.43497 8.16144 4.31491 8.40923 4.23452 8.58617C4.1943 8.67471 4.16391 8.7457 4.14298 8.79616C4.13251 8.82139 4.1244 8.84151 4.11859 8.85613C4.11568 8.86344 4.11336 8.86938 4.1116 8.8739C4.11072 8.87616 4.10998 8.87807 4.10939 8.87962C4.10909 8.88039 4.10883 8.88108 4.1086 8.88167C4.10849 8.88196 4.10834 8.88234 4.10829 8.88249C4.10815 8.88284 4.10803 8.88317 4.80899 9.14991C5.50996 9.41664 5.50985 9.41692 5.50975 9.41719C5.50973 9.41725 5.50964 9.41749 5.50959 9.4176C5.5095 9.41784 5.50945 9.41798 5.50942 9.41804C5.50938 9.41816 5.50947 9.41792 5.50969 9.41734C5.51014 9.41619 5.51113 9.41365 5.51267 9.40979C5.51574 9.40206 5.52099 9.38901 5.52846 9.37101C5.5434 9.335 5.56719 9.27924 5.60018 9.20664C5.66621 9.0613 5.76871 8.84925 5.91031 8.59341C6.19442 8.08008 6.63084 7.39971 7.23855 6.72958C8.44912 5.39466 10.3098 4.12566 13.0216 4.23922L13.0844 2.74053Z"
                                            fill="currentColor"></path>
                                            <path d="M8.78337 9.33604C8.72981 9.40713 8.65805 9.46292 8.57443 9.49703L3.1072 11.6951C2.95672 11.7552 2.78966 11.7352 2.66427 11.6407C2.53887 11.5462 2.47359 11.3912 2.48993 11.2299L3.09576 5.36863C3.11367 5.19823 3.22102 5.04666 3.37711 4.97402C3.5331 4.9005 3.71173 4.91728 3.84442 5.01726L8.70581 8.68052C8.8385 8.78051 8.90387 8.94759 8.8762 9.1178C8.86358 9.19825 8.83082 9.27308 8.78337 9.33604" fill="currentColor"></path>
                                        </svg> 
                                        </button>

                                        <button
                                            className="btn btn-primary btn-sm"
                                            data-bs-toggle="modal"
                                            data-bs-target="#addTiraModal"
                                            onClick={resetAddAvisoModal}
                                        >
                                            <span className="btn-inner mr-5">
                                                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" className="icon-24">
                                                    <path
                                                        d="M12 4V20M20 12H4"
                                                        stroke="currentColor"
                                                        strokeWidth="2"
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                    />
                                                </svg>
                                            </span>
                                            Agregar Tira de avisos
                                        </button>
                                    </div>
                                </div>
                            </div>
                            <div class="card-body px-0">
                                    <div class="table-responsive">
                                        <table id="user-list-table" class="table table-striped" role="grid" data-toggle="data-table">
                                            <thead>
                                                <tr class="ligth">
                                                <th># Orden</th>
                                                <th>Aviso</th>
                                                <th>Fecha Inicio</th>
                                                <th>Fecha Fin</th>
                                                <th>Estado</th>
                                                <th>Acción</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {loadingAvisos ? (
                                                    <tr>
                                                        <td colSpan="6" className="text-center">
                                                            <div className="d-flex flex-column align-items-center justify-content-center gap-2">
                                                                <div className="col-md-12 col-sm-4 mb-4">
                                                                    <div className="iq-loader-box">
                                                                        <div className="iq-loader-1"></div>
                                                                    </div>
                                                                </div>

                                                                <span className="text-muted">
                                                                    Cargando elementos de la tira de avisos...
                                                                </span>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ) : avisosError ? (
                                                    <tr>
                                                        <td colSpan="6" className="text-center text-danger">
                                                            {avisosError}
                                                        </td>
                                                    </tr>
                                                ) : filteredAvisosList.length === 0 ? (
                                                    <tr>
                                                        <td colSpan="6" className="text-center">
                                                            No hay avisos registrados
                                                        </td>
                                                    </tr>
                                                ) : (
                                                    paginatedItems.map((aviso, index) => (
                                                        <tr key={aviso.Aviso_ID}>
                                                            <td>{startItem + index}</td>

                                                            <td>
                                                                <div className="d-flex flex-column">
                                                                    <strong>{aviso.Aviso_Texto}</strong>

                                                                    {aviso.Aviso_Boton_URL ? (
                                                                        <small className="text-muted">
                                                                            {aviso.Aviso_Boton_URL}
                                                                        </small>
                                                                    ) : null}
                                                                </div>
                                                            </td>

                                                            <td>{formatAvisoDate(aviso.Aviso_Fecha_Inicio)}</td>

                                                            <td>{formatAvisoDate(aviso.Aviso_Fecha_Fin)}</td>

                                                            <td>{renderAvisoStatusBadge(aviso)}</td>

                                                            <td>
                                                                <div className="d-flex">
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-icon btn-info-subtle ms-2"
                                                                        data-bs-toggle="modal"
                                                                        data-bs-target="#viewAvisoModal"
                                                                        onClick={() => handleViewAviso(aviso.Aviso_ID)}
                                                                    >
                                                                    <span className="btn-inner">
                                                                        <svg
                                                                            className="icon-32"
                                                                            width="32"
                                                                            viewBox="0 0 24 24"
                                                                            fill="none"
                                                                            xmlns="http://www.w3.org/2000/svg"
                                                                        >
                                                                            <path
                                                                                opacity="0.4"
                                                                                fillRule="evenodd"
                                                                                clipRule="evenodd"
                                                                                d="M17.7366 6.04606C19.4439 7.36388 20.8976 9.29455 21.9415 11.7091C22.0195 11.8924 22.0195 12.1067 21.9415 12.2812C19.8537 17.1103 16.1366 20 12 20H11.9902C7.86341 20 4.14634 17.1103 2.05854 12.2812C1.98049 12.1067 1.98049 11.8924 2.05854 11.7091C4.14634 6.87903 7.86341 4 11.9902 4H12C14.0683 4 16.0293 4.71758 17.7366 6.04606ZM8.09756 12C8.09756 14.1333 9.8439 15.8691 12 15.8691C14.1463 15.8691 15.8927 14.1333 15.8927 12C15.8927 9.85697 14.1463 8.12121 12 8.12121C9.8439 8.12121 8.09756 9.85697 8.09756 12Z"
                                                                                fill="currentColor"
                                                                            />
                                                                            <path
                                                                                d="M14.4308 11.997C14.4308 13.3255 13.3381 14.4115 12.0015 14.4115C10.6552 14.4115 9.5625 13.3255 9.5625 11.997C9.5625 11.8321 9.58201 11.678 9.61128 11.5228H9.66006C10.743 11.5228 11.621 10.6695 11.6601 9.60184C11.7674 9.58342 11.8845 9.57275 12.0015 9.57275C13.3381 9.57275 14.4308 10.6588 14.4308 11.997Z"
                                                                                fill="currentColor"
                                                                            />
                                                                        </svg>
                                                                    </span>
                                                                    </button>

                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-icon btn-warning-subtle ms-2"
                                                                        data-bs-toggle="modal"
                                                                        data-bs-target="#editAvisoModal"
                                                                        onClick={() => handleLoadEditAviso(aviso.Aviso_ID)}
                                                                    >
                                                                        <span className="btn-inner">
                                                                        <svg
                                                                            className="icon-32"
                                                                            width="32"
                                                                            viewBox="0 0 24 24"
                                                                            fill="none"
                                                                            xmlns="http://www.w3.org/2000/svg"
                                                                        >
                                                                            <path
                                                                                opacity="0.4"
                                                                                d="M19.9927 18.9534H14.2984C13.7429 18.9534 13.291 19.4124 13.291 19.9767C13.291 20.5422 13.7429 21.0001 14.2984 21.0001H19.9927C20.5483 21.0001 21.0001 20.5422 21.0001 19.9767C21.0001 19.4124 20.5483 18.9534 19.9927 18.9534Z"
                                                                                fill="currentColor"
                                                                            />
                                                                            <path
                                                                                d="M10.309 6.90385L15.7049 11.2639C15.835 11.3682 15.8573 11.5596 15.7557 11.6929L9.35874 20.0282C8.95662 20.5431 8.36402 20.8344 7.72908 20.8452L4.23696 20.8882C4.05071 20.8903 3.88775 20.7613 3.84542 20.5764L3.05175 17.1258C2.91419 16.4915 3.05175 15.8358 3.45388 15.3306L9.88256 6.95545C9.98627 6.82108 10.1778 6.79743 10.309 6.90385Z"
                                                                                fill="currentColor"
                                                                            />
                                                                            <path
                                                                                opacity="0.4"
                                                                                d="M18.1208 8.66544L17.0806 9.96401C16.9758 10.0962 16.7874 10.1177 16.6573 10.0124C15.3927 8.98901 12.1545 6.36285 11.2561 5.63509C11.1249 5.52759 11.1069 5.33625 11.2127 5.20295L12.2159 3.95706C13.126 2.78534 14.7133 2.67784 15.9938 3.69906L17.4647 4.87078C18.0679 5.34377 18.47 5.96726 18.6076 6.62299C18.7663 7.3443 18.597 8.0527 18.1208 8.66544Z"
                                                                                fill="currentColor"
                                                                            />
                                                                        </svg>
                                                                    </span>
                                                                    </button>

                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-icon btn-danger-subtle ms-2"
                                                                        onClick={() => handleDeleteAviso(aviso.Aviso_ID, aviso.Aviso_Texto)}
                                                                    >
                                                                        <span className="btn-inner">
                                                                        <svg
                                                                            className="icon-32"
                                                                            width="32"
                                                                            viewBox="0 0 24 24"
                                                                            fill="none"
                                                                            xmlns="http://www.w3.org/2000/svg"
                                                                        >
                                                                            <path
                                                                                opacity="0.4"
                                                                                d="M19.643 9.48851C19.643 9.5565 19.11 16.2973 18.8056 19.1342C18.615 20.8751 17.4927 21.9311 15.8092 21.9611C14.5157 21.9901 13.2494 22.0001 12.0036 22.0001C10.6809 22.0001 9.38741 21.9901 8.13185 21.9611C6.50477 21.9221 5.38147 20.8451 5.20057 19.1342C4.88741 16.2873 4.36418 9.5565 4.35445 9.48851C4.34473 9.28351 4.41086 9.08852 4.54507 8.93053C4.67734 8.78453 4.86796 8.69653 5.06831 8.69653H18.9388C19.1382 8.69653 19.3191 8.78453 19.4621 8.93053C19.5953 9.08852 19.6624 9.28351 19.643 9.48851Z"
                                                                                fill="currentColor"
                                                                            />
                                                                            <path
                                                                                d="M21 5.97686C21 5.56588 20.6761 5.24389 20.2871 5.24389H17.3714C16.7781 5.24389 16.2627 4.8219 16.1304 4.22692L15.967 3.49795C15.7385 2.61698 14.9498 2 14.0647 2H9.93624C9.0415 2 8.26054 2.61698 8.02323 3.54595L7.87054 4.22792C7.7373 4.8219 7.22185 5.24389 6.62957 5.24389H3.71385C3.32386 5.24389 3 5.56588 3 5.97686V6.35685C3 6.75783 3.32386 7.08982 3.71385 7.08982H20.2871C20.6761 7.08982 21 6.75783 21 6.35685V5.97686Z"
                                                                                fill="currentColor"
                                                                            />
                                                                        </svg>
                                                                    </span>
                                                                    </button>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ))
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                            </div>
                            {filteredAvisosList.length > 0 ? (
                                <UniversalPagination
                                    currentPage={currentPage}
                                    totalPages={totalPages}
                                    totalItems={totalItems}
                                    startItem={startItem}
                                    endItem={endItem}
                                    visiblePages={visiblePages}
                                    onPageChange={goToPage}
                                    onPrevious={goToPreviousPage}
                                    onNext={goToNextPage}
                                    hasPreviousPage={hasPreviousPage}
                                    hasNextPage={hasNextPage}
                                />
                            ) : null}
                        </div>
                    </div>
                </div>
            </div>
        </main>

        {/* Modal para agregar tira de avisos */}
        <div
            className="modal fade"
            id="addTiraModal"
            tabIndex="-1"
            aria-labelledby="addTiraModalLabel"
            aria-hidden="true"
        >
            <div className="modal-dialog modal-dialog-centered modal-xl">
                <div className="modal-content">
                    <form onSubmit={handleAddAviso}>
                        <div className="modal-header">
                            <h5 className="modal-title" id="addTiraModalLabel">
                                Agregar Tira de Avisos
                            </h5>
                            <button
                                type="button"
                                className="btn-close"
                                data-bs-dismiss="modal"
                                aria-label="Close"
                                disabled={addingAviso}
                            ></button>
                        </div>

                        <div className="modal-body">
                            <div className="row mb-3">
                                <div className="col-md-12">
                                    <label className="form-label">
                                        Texto del aviso: <span className="text-danger">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="aviso_texto"
                                        className="form-control"
                                        maxLength={255}
                                        value={avisoForm.aviso_texto}
                                        onChange={handleAvisoInputChange}
                                        disabled={addingAviso}
                                        placeholder="Escribe el texto que aparecerá en la tira de avisos"
                                    />
                                    <small className="text-muted">
                                        {avisoForm.aviso_texto.length}/255 caracteres
                                    </small>
                                </div>
                            </div>

                            <div className="row mb-3">
                                <div className="col-md-6">
                                    <label className="form-label">Texto botón</label>
                                    <input
                                        type="text"
                                        name="aviso_boton_texto"
                                        className="form-control"
                                        value={avisoForm.aviso_boton_texto}
                                        onChange={handleAvisoInputChange}
                                        disabled={addingAviso}
                                        placeholder="Ej. Ver más"
                                    />
                                </div>

                                <div className="col-md-6">
                                    <label className="form-label">URL botón</label>
                                    <input
                                        type="text"
                                        name="aviso_boton_url"
                                        className="form-control"
                                        value={avisoForm.aviso_boton_url}
                                        onChange={handleAvisoInputChange}
                                        disabled={addingAviso}
                                        placeholder="https://... o /ruta-interna"
                                    />
                                </div>
                            </div>

                            <div className="row mb-3">
                                <div className="col-md-4">
                                    <label className="form-label">
                                        Fecha inicio: <span className="text-danger">*</span>
                                    </label>
                                    <input
                                        type="datetime-local"
                                        name="aviso_fecha_inicio"
                                        className="form-control"
                                        value={avisoForm.aviso_fecha_inicio}
                                        onChange={handleAvisoInputChange}
                                        disabled={addingAviso}
                                    />
                                </div>

                                <div className="col-md-4">
                                    <label className="form-label">
                                        Fecha fin: <span className="text-danger">*</span>
                                    </label>
                                    <input
                                        type="datetime-local"
                                        name="aviso_fecha_fin"
                                        className="form-control"
                                        value={avisoForm.aviso_fecha_fin}
                                        onChange={handleAvisoInputChange}
                                        disabled={addingAviso}
                                    />
                                </div>

                                <div className="col-md-4">
                                    <label className="form-label">Activo</label>
                                    <select
                                        name="aviso_activo"
                                        className="form-select"
                                        value={avisoForm.aviso_activo}
                                        onChange={handleAvisoInputChange}
                                        disabled={addingAviso}
                                    >
                                        <option value={1}>Sí</option>
                                        <option value={0}>No</option>
                                    </select>
                                </div>
                            </div>

                            {avisoSuccessMessage ? (
                                <div className="mb-3 alert alert-left alert-success alert-dismissible fade show" role="alert">
                                    <span>{avisoSuccessMessage}</span>
                                    <button
                                        type="button"
                                        className="btn-close"
                                        aria-label="Close"
                                        onClick={() => setAvisoSuccessMessage("")}
                                    ></button>
                                </div>
                            ) : null}

                            {avisoInfoMessage ? (
                                <div className="mb-3 alert alert-top alert-info alert-dismissible fade show" role="alert">
                                    <span>{avisoInfoMessage}</span>
                                    <button
                                        type="button"
                                        className="btn-close"
                                        aria-label="Close"
                                        onClick={() => setAvisoInfoMessage("")}
                                    ></button>
                                </div>
                            ) : null}

                            {avisoErrorMessage ? (
                                <div className="alert alert-bottom alert-danger alert-dismissible fade show" role="alert">
                                    <span>{avisoErrorMessage}</span>
                                    <button
                                        type="button"
                                        className="btn-close"
                                        aria-label="Close"
                                        onClick={() => setAvisoErrorMessage("")}
                                    ></button>
                                </div>
                            ) : null}
                        </div>

                        <div className="modal-footer">
                            <button
                                type="button"
                                className="btn btn-danger"
                                data-bs-dismiss="modal"
                                disabled={addingAviso}
                            >
                                Cerrar
                            </button>

                            <button type="submit" className="btn btn-primary" disabled={addingAviso}>
                                {addingAviso ? "Agregando..." : "Agregar"}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>

        {/* Modal para ver tira de avisos */}
        <div
            className="modal fade"
            id="viewAvisoModal"
            tabIndex="-1"
            aria-labelledby="viewAvisoModalLabel"
            aria-hidden="true"
        >
            <div className="modal-dialog modal-dialog-centered modal-lg">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title" id="viewAvisoModalLabel">
                            Detalle de Tira de Avisos
                        </h5>
                        <button
                            type="button"
                            className="btn-close"
                            data-bs-dismiss="modal"
                            aria-label="Close"
                            onClick={resetViewAvisoModal}
                        ></button>
                    </div>

                    <div className="modal-body">
                        {loadingViewAviso ? (
                            <div className="text-center py-4">
                                Cargando detalle del aviso...
                            </div>
                        ) : viewAvisoError ? (
                            <div className="alert alert-danger" role="alert">
                                {viewAvisoError}
                            </div>
                        ) : !viewAvisoData ? (
                            <div className="alert alert-info" role="alert">
                                No hay información disponible del aviso.
                            </div>
                        ) : (
                            <div className="row">
                                <div className="col-md-12 mb-3">
                                    <label className="form-label fw-bold">Aviso</label>
                                    <div className="form-control " style={{ height: "auto", minHeight: "46px" }}>
                                        {viewAvisoData.Aviso_Texto || "-"}
                                    </div>
                                </div>

                                <div className="col-md-6 mb-3">
                                    <label className="form-label fw-bold">Texto botón</label>
                                    <div className="form-control ">
                                        {viewAvisoData.Aviso_Boton_Texto || "-"}
                                    </div>
                                </div>

                                <div className="col-md-6 mb-3">
                                    <label className="form-label fw-bold">URL botón</label>
                                    <div className="form-control ">
                                        {viewAvisoData.Aviso_Boton_URL || "-"}
                                    </div>
                                </div>

                                <div className="col-md-6 mb-3">
                                    <label className="form-label fw-bold">Fecha inicio</label>
                                    <div className="form-control ">
                                        {formatAvisoDateTime(viewAvisoData.Aviso_Fecha_Inicio)}
                                    </div>
                                </div>

                                <div className="col-md-6 mb-3">
                                    <label className="form-label fw-bold">Fecha fin</label>
                                    <div className="form-control ">
                                        {formatAvisoDateTime(viewAvisoData.Aviso_Fecha_Fin)}
                                    </div>
                                </div>

                                <div className="col-md-4 mb-3">
                                    <label className="form-label fw-bold">Activo</label>
                                    <div className="form-control ">
                                        {Number(viewAvisoData.Aviso_Activo) === 1 ? "Sí" : "No"}
                                    </div>
                                </div>

                                <div className="col-md-4 mb-3">
                                    <label className="form-label fw-bold">Clicks totales</label>
                                    <div className="form-control ">
                                        {viewAvisoData.Aviso_Total_Clicks ?? 0}
                                    </div>
                                </div>

                                <div className="col-md-4 mb-3">
                                    <label className="form-label fw-bold">Clicks únicos</label>
                                    <div className="form-control ">
                                        {viewAvisoData.Aviso_Clicks_Unicos ?? 0}
                                    </div>
                                </div>

                                <div className="col-md-4 mb-3">
                                    <label className="form-label fw-bold">Creado por</label>
                                    <div className="form-control ">
                                        {`${viewAvisoData.Creador_Nombre || ""} ${viewAvisoData.Creador_Apellido || ""}`.trim() || "-"}
                                    </div>
                                </div>

                                <div className="col-md-4 mb-3">
                                    <label className="form-label fw-bold">Actualizado por</label>
                                    <div className="form-control ">
                                        {`${viewAvisoData.Actualizador_Nombre || ""} ${viewAvisoData.Actualizador_Apellido || ""}`.trim() || "-"}
                                    </div>
                                </div>

                                <div className="col-md-4 mb-3">
                                    <label className="form-label fw-bold">Última actualización</label>
                                    <div className="form-control ">
                                        {formatAvisoDateTime(viewAvisoData.Aviso_Act)}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="modal-footer">
                        <button
                            type="button"
                            className="btn btn-danger"
                            data-bs-dismiss="modal"
                            onClick={resetViewAvisoModal}
                        >
                            Cerrar
                        </button>
                    </div>
                </div>
            </div>
        </div>

        {/* Modal para editar tira de avisos */}
        <div
            className="modal fade"
            id="editAvisoModal"
            tabIndex="-1"
            aria-labelledby="editAvisoModalLabel"
            aria-hidden="true"
        >
            <div className="modal-dialog modal-dialog-centered modal-xl">
                <div className="modal-content">
                    <form onSubmit={handleUpdateAviso}>
                        <div className="modal-header">
                            <h5 className="modal-title" id="editAvisoModalLabel">
                                Editar Tira de Avisos
                            </h5>
                            <button
                                type="button"
                                className="btn-close"
                                data-bs-dismiss="modal"
                                aria-label="Close"
                                disabled={savingEditAviso}
                                onClick={resetEditAvisoModal}
                            ></button>
                        </div>

                        <div className="modal-body">
                            {loadingEditAviso ? (
                                <div className="text-center py-4">
                                    Cargando información del aviso...
                                </div>
                            ) : (
                                <>
                                    <div className="row mb-3">
                                        <div className="col-md-12">
                                            <label className="form-label">
                                                Texto del aviso: <span className="text-danger">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                name="aviso_texto"
                                                className="form-control"
                                                maxLength={255}
                                                value={editAvisoForm.aviso_texto}
                                                onChange={handleEditAvisoInputChange}
                                                disabled={savingEditAviso}
                                            />
                                            <small className="text-muted">
                                                {editAvisoForm.aviso_texto.length}/255 caracteres
                                            </small>
                                        </div>
                                    </div>

                                    <div className="row mb-3">
                                        <div className="col-md-6">
                                            <label className="form-label">Texto botón</label>
                                            <input
                                                type="text"
                                                name="aviso_boton_texto"
                                                className="form-control"
                                                value={editAvisoForm.aviso_boton_texto}
                                                onChange={handleEditAvisoInputChange}
                                                disabled={savingEditAviso}
                                            />
                                        </div>

                                        <div className="col-md-6">
                                            <label className="form-label">URL botón</label>
                                            <input
                                                type="text"
                                                name="aviso_boton_url"
                                                className="form-control"
                                                value={editAvisoForm.aviso_boton_url}
                                                onChange={handleEditAvisoInputChange}
                                                disabled={savingEditAviso}
                                            />
                                        </div>
                                    </div>

                                    <div className="row mb-3">
                                        <div className="col-md-4">
                                            <label className="form-label">
                                                Fecha inicio: <span className="text-danger">*</span>
                                            </label>
                                            <input
                                                type="datetime-local"
                                                name="aviso_fecha_inicio"
                                                className="form-control"
                                                value={editAvisoForm.aviso_fecha_inicio}
                                                onChange={handleEditAvisoInputChange}
                                                disabled={savingEditAviso}
                                            />
                                        </div>

                                        <div className="col-md-4">
                                            <label className="form-label">
                                                Fecha fin: <span className="text-danger">*</span>
                                            </label>
                                            <input
                                                type="datetime-local"
                                                name="aviso_fecha_fin"
                                                className="form-control"
                                                value={editAvisoForm.aviso_fecha_fin}
                                                onChange={handleEditAvisoInputChange}
                                                disabled={savingEditAviso}
                                            />
                                        </div>

                                        <div className="col-md-4">
                                            <label className="form-label">Activo</label>
                                            <select
                                                name="aviso_activo"
                                                className="form-select"
                                                value={editAvisoForm.aviso_activo}
                                                onChange={handleEditAvisoInputChange}
                                                disabled={savingEditAviso}
                                            >
                                                <option value={1}>Sí</option>
                                                <option value={0}>No</option>
                                            </select>
                                        </div>
                                    </div>

                                    {editAvisoSuccessMessage ? (
                                        <div className="mb-3 alert alert-left alert-success alert-dismissible fade show" role="alert">
                                            <span>{editAvisoSuccessMessage}</span>
                                            <button
                                                type="button"
                                                className="btn-close"
                                                aria-label="Close"
                                                onClick={() => setEditAvisoSuccessMessage("")}
                                            ></button>
                                        </div>
                                    ) : null}

                                    {editAvisoInfoMessage ? (
                                        <div className="mb-3 alert alert-top alert-info alert-dismissible fade show" role="alert">
                                            <span>{editAvisoInfoMessage}</span>
                                            <button
                                                type="button"
                                                className="btn-close"
                                                aria-label="Close"
                                                onClick={() => setEditAvisoInfoMessage("")}
                                            ></button>
                                        </div>
                                    ) : null}

                                    {editAvisoErrorMessage ? (
                                        <div className="alert alert-bottom alert-danger alert-dismissible fade show" role="alert">
                                            <span>{editAvisoErrorMessage}</span>
                                            <button
                                                type="button"
                                                className="btn-close"
                                                aria-label="Close"
                                                onClick={() => setEditAvisoErrorMessage("")}
                                            ></button>
                                        </div>
                                    ) : null}
                                </>
                            )}
                        </div>

                        <div className="modal-footer">
                            <button
                                type="button"
                                className="btn btn-danger"
                                data-bs-dismiss="modal"
                                disabled={savingEditAviso}
                                onClick={resetEditAvisoModal}
                            >
                                Cerrar
                            </button>

                            <button
                                type="submit"
                                className="btn btn-primary"
                                disabled={savingEditAviso || loadingEditAviso}
                            >
                                {savingEditAviso ? "Guardando..." : "Guardar cambios"}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
        </>
    )
}

export default CintilloList